import sortBy from 'lodash.sortby';
import filter from 'lodash.filter';
import find from 'lodash.find';
import staticLegalInsurances, { legalInsuranceAliases } from './data/legalInsurances';
import { GetIntegratedPartnersQuery, GetPartnerBySlug, GetProductSelection } from '../gql';
import { IntegratedPartnerType, ProductType } from '../types';
import getClient from '../../react-commons/utils/graphql';

export interface Partner {
  id: string;
  name: string;
  options?: string[];
}

export interface PartnersByType {
  default: Partner;
  legalInsurances: Array<Partner>;
  options?: string[];
}

export interface PartnerProductAttribute {
  id: string;
  description: string;
  metaDescription: string;
}

export interface PartnerProduct {
  id: string;
  name: string;
  price?: number;
  initialPrice?: number;
  categoryId: string;
  requiresPayment: boolean;
  attributesIncluded: Array<PartnerProductAttribute>;
  attributesExcluded: Array<PartnerProductAttribute>;
  attributesHighlighted: Array<PartnerProductAttribute>;
  type?: 'private' | 'business';
  requiresInsuranceProof: boolean;
  requiresBillingConsent: boolean;
  integratedPartner: {
    partnerSettings: {
      productLogo?: string;
    };
    referenceLabelCase: string;
    referenceLabelContract: string;
    referenceLabelVoucher?: string;
    type: string;
    name: string;
    id: string;
    conditionText?: string;
    preResultPromoText?: string;
    options?: string[];
  };
  defaultSelected: boolean;
  unavailable?: boolean;
  highlighted?: boolean;
  hidePrice?: boolean;
}

export interface ProductSelectionInput {
  type: ProductType;
  affiliatePartnerId?: string;
  integratedPartnerIds: string[];
  filter?: string;
}

const removeDuplicates = (staticPartners: Array<string>, integratedPartners: Array<Partner>): Array<string> => {
  return filter(staticPartners, (l: string) => !find(integratedPartners, { name: l }));
};

const mergePartners = (staticPartners: Array<string>, integratedPartners: Array<Partner>): Array<Partner> => {
  return sortBy(
    [
      ...integratedPartners,
      ...removeDuplicates(staticPartners, integratedPartners).map((l: string): Partner => ({ id: l, name: l })),
    ],
    [(partner: Partner) => partner.name.toLocaleLowerCase().replace('ö', 'oe').replace('ä', 'ae').replace('ü', 'ue')],
  );
};

let partners: PartnersByType | undefined;

const getLegalInsurances = (integratedLegalInsurances: Array<Partner>) => {
  return mergePartners(staticLegalInsurances, integratedLegalInsurances);
};

const addAliasLegalInsurances = (integratedLegalInsurances: Array<Partner>) =>
  integratedLegalInsurances.map((p) => {
    if (legalInsuranceAliases[p.name]) {
      const aliases = legalInsuranceAliases[p.name];
      return { ...p, name: `${p.name} / ${aliases.join(' / ')}` };
    }
    return p;
  });

// @TODO once we refactor the data to come from the api, all partners will all have an id
// we should probably add an attribute on the partner object like `partner.isIntegrated = true`
export const isIntegratedPartner = (partner?: Partner): boolean => {
  return !!partner && partner.id !== partner.name;
};

export default {
  getPartners(): Promise<PartnersByType> {
    if (partners) return Promise.resolve(partners);
    return getClient()
      .runQuery(GetIntegratedPartnersQuery, undefined, false, 'GET')
      .then((res: any) => {
        return {
          default: res.default[0],
          legalInsurances: addAliasLegalInsurances(getLegalInsurances(res.legalInsurances)),
        };
      })
      .then((partners: PartnersByType) => {
        return partners;
      });
  },

  getPartnerPackages(input: ProductSelectionInput): Promise<PartnerProduct[]> {
    return getClient()
      .runQuery<{ ProductSelection: PartnerProduct[] }>(GetProductSelection, { input })
      .then((result: { ProductSelection: PartnerProduct[] }) => {
        return result.ProductSelection;
      });
  },

  getPartnerBySlug(slug: string): Promise<IntegratedPartnerType> {
    return getClient()
      .runQuery<{ IntegratedPartnerBySlug: IntegratedPartnerType }>(GetPartnerBySlug, { slug }, false, 'GET')
      .then((res: { IntegratedPartnerBySlug: IntegratedPartnerType }) => {
        return res.IntegratedPartnerBySlug;
      });
  },
};
